import './SocialUpdatePhoto.scss';

import arrowBackFull from 'assets/svgs/arrow-back-full.svg';
import closeIcon from 'assets/svgs/close.svg';
import DnDFilePicker from 'components/DnDImgPicker/DnDImgPicker';
import { canvasPreview } from 'components/ImageWithCrop/generateCanvas';
import ImgCropper from 'components/ImgCropper/ImgCropper';
import { uploadImg } from 'helpers/uploadImg';
import { useGetPetProfile } from 'hooks/useGetPetProfile';
import useIsMobile from 'hooks/useIsMobile';
import { observer } from 'mobx-react-lite';
import { IconImage } from 'petdna-lib-uicomponents/components';
import React, {
  FC, useCallback, useEffect, useRef, useState,
} from 'react';
import { Collapse, Fade } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { convertToPixelCrop, Crop } from 'react-image-crop';
import { useClickAway } from 'react-use';

import SocialUpdatePhotoPreview from './SocialUpdatePhotoPreview/SocialUpdatePhotoPreview';
import SocialUpdatePhotoSource from './SocialUpdatePhotoSource/SocialUpdatePhotoSource';

interface ISocialUpdatePhotoProps {
  isUpdatePhotoModeOpen: boolean;
  onClose: () => void;
  toggleIsUpdatePhotoOpen: () => void;
  loadShareData: (imageId?: string) => Promise<void>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const SocialUpdatePhoto: FC<ISocialUpdatePhotoProps> = observer(({
  isUpdatePhotoModeOpen,
  onClose,
  setIsLoading,
  toggleIsUpdatePhotoOpen,
  loadShareData,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const imageRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [imgFile, setImgFile] = useState<Blob>();
  const [previewImg, setPreviewImg] = useState('');
  const [cropImgFile, setCropImgFile] = useState<Blob>();
  const [previewCropImg, setPreviewCropImg] = useState('');
  const { testsStore } = useGetPetProfile();
  const { petProfile } = testsStore;

  useEffect(() => {
    if (imgFile) {
      setPreviewImg(URL.createObjectURL(imgFile));
    } else {
      setPreviewImg('');
    }
  }, [imgFile]);

  useEffect(() => {
    if (cropImgFile) {
      setPreviewCropImg(URL.createObjectURL(cropImgFile));
    } else {
      setPreviewCropImg('');
    }
  }, [cropImgFile]);

  useEffect(() => {
    setImgFile(undefined);
  }, [isUpdatePhotoModeOpen]);

  const onCancelClick = useCallback(() => {
    if (cropImgFile) {
      setCropImgFile(undefined);
    } else if (imgFile) {
      setImgFile(undefined);
    } else {
      toggleIsUpdatePhotoOpen();
    }
  }, [
    cropImgFile,
    imgFile,
    toggleIsUpdatePhotoOpen,
  ]);

  const onCropImage = useCallback(() => {
    setCropImgFile(undefined);
  }, []);

  const onSavePhotoClick = useCallback(async () => {
    if (!imageRef.current || !crop) return;
    const { width, height } = imageRef.current;

    const croppedCanvas = await canvasPreview(
      imageRef.current,
      convertToPixelCrop(crop, width, height),
    );

    croppedCanvas.toBlob((blob) => {
      setCropImgFile(blob ?? imgFile);
    });
  }, [imgFile, crop]);

  const onUpdateImage = useCallback(async () => {
    if (!petProfile || !cropImgFile) return;
    setIsLoading(true);
    const imageId = await uploadImg(petProfile?.testGUID, cropImgFile);
    loadShareData(imageId);
    toggleIsUpdatePhotoOpen();
  }, [
    petProfile,
    cropImgFile,
    setIsLoading,
    loadShareData,
    toggleIsUpdatePhotoOpen,
  ]);

  const onUpdateSaveImage = useCallback(async () => {
    if (!petProfile || !cropImgFile) return;
    setIsLoading(true);
    const imageId = await uploadImg(petProfile?.testGUID, cropImgFile);
    testsStore.updatePetProfiles(petProfile?.testGUID, { imageId });
    loadShareData();
    toggleIsUpdatePhotoOpen();
  }, [
    testsStore,
    petProfile,
    cropImgFile,
    setIsLoading,
    loadShareData,
    toggleIsUpdatePhotoOpen,
  ]);

  const handleClose = useCallback(() => {
    toggleIsUpdatePhotoOpen();
    onClose();
  }, [
    onClose,
    toggleIsUpdatePhotoOpen,
  ]);

  const handleClickAway = useCallback(() => {
    if (!isUpdatePhotoModeOpen) return;
    toggleIsUpdatePhotoOpen();
  }, [
    isUpdatePhotoModeOpen,
    toggleIsUpdatePhotoOpen,
  ]);

  useClickAway(contentRef, handleClickAway);

  return (
    <div className="social-update-photo">
      {isMobile && !imgFile ? (
        <SocialUpdatePhotoSource
          isUpdatePhotoModeOpen={isUpdatePhotoModeOpen}
          onSetImg={setImgFile}
          onClose={toggleIsUpdatePhotoOpen}
        />
      ) : (
        <>
          <Fade in={isUpdatePhotoModeOpen} mountOnEnter unmountOnExit>
            <div className="social-update-photo__backdrop" />
          </Fade>
          <Collapse in={isUpdatePhotoModeOpen} dimension="width">
            <div ref={contentRef} className="social-update-photo__content">
              <div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleClose}
                >
                  <IconImage className="close-icon" src={closeIcon} />
                </button>
              </div>
              <div className="social-update-photo__title-wrapper">
                <button
                  type="button"
                  className="btn"
                  onClick={onCancelClick}
                >
                  <IconImage className="back-icon" src={arrowBackFull} color="#1E4537" />
                </button>
                <div className="social-update-photo__title font-serif">
                  {t('socialSharingModal.updatePhoto')}
                </div>
              </div>
              <div className="social-update-photo__file-picker">
                {!previewCropImg && imgFile ? (
                  <ImgCropper
                    img={previewImg}
                    ref={imageRef}
                    keepSelection
                    circularCrop
                    crop={crop}
                    setCrop={setCrop}
                  />
                ) : null}
                {!previewCropImg && !imgFile ? (
                  <DnDFilePicker img={imgFile} onSetImg={setImgFile} />
                ) : null}
                {previewCropImg ? (
                  <SocialUpdatePhotoPreview
                    img={previewCropImg}
                    onCropImage={onCropImage}
                  />
                ) : null}
              </div>
              <div className="social-update-photo__buttons">
                {!previewCropImg ? (
                  <>
                    <button
                      onClick={onCancelClick}
                      type="button"
                      className="btn btn-cancel"
                    >
                      {t('socialSharingModal.cancel')}
                    </button>
                    <button
                      onClick={onSavePhotoClick}
                      disabled={!imgFile}
                      type="button"
                      className="btn btn-dark"
                    >
                      {t('socialSharingModal.savePhoto')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onUpdateImage}
                      type="button"
                      className="btn btn-cancel"
                    >
                      {t('socialSharingModal.dontSave')}
                    </button>
                    <button
                      onClick={onUpdateSaveImage}
                      type="button"
                      className="btn btn-dark"
                    >
                      {t('socialSharingModal.saveAsProfilePhoto')}
                    </button>
                  </>
                )}
              </div>
            </div>
          </Collapse>
        </>
      )}
    </div>
  );
});

export default SocialUpdatePhoto;
